// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type JwtClaims = Record<string, any>;

// Refresh while a comfortable slice of the pass's 3600s lifetime remains, so a
// failed refresh has later page loads to recover on before the pass dies.
const FRESHNESS_MARGIN_SECONDS = 5 * 60;

// Tolerance for a device clock running ahead of the server's. A false
// "expired" is a sign-out, so a pass is only expired once it is past this.
const CLOCK_SKEW_TOLERANCE_SECONDS = 2 * 60;

const PASS_KEY_ID_PREFIX = 'pass-';

// Answers null rather than throwing: the input is a cookie or localStorage
// value read on the boot path, and a throw there takes the page down.
const decodeSegment = (token: string, index: number): JwtClaims | null => {
  try {
    const base64Url = token.split('.')[index];

    if (!base64Url) {
      return null;
    }

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const claims = JSON.parse(jsonPayload);

    return typeof claims === 'object' && claims !== null ? claims : null;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return null;
  }
};

export const parseJwt = (token: string): JwtClaims | null =>
  decodeSegment(token, 1);

const parseJwtHeader = (token: string): JwtClaims | null =>
  decodeSegment(token, 0);

// Our signer's `kid` is `pass-1`/`pass-prod-1`, so the prefix outlives a key
// rotation; the retired pool's is a base64 thumbprint. Fails open on a header
// we cannot read — a wrong answer here signs a real customer out.
export const isForeignPass = (token: string): boolean => {
  const kid = parseJwtHeader(token)?.kid;

  if (typeof kid !== 'string' || kid === '') {
    return false;
  }

  return !kid.startsWith(PASS_KEY_ID_PREFIX);
};

export const passExpiresAt = (token: string): number | null => {
  const exp = parseJwt(token)?.exp;

  if (typeof exp !== 'number' || !Number.isFinite(exp) || exp <= 0) {
    return null;
  }

  // `exp` is in seconds; `Date.now()` is in milliseconds.
  return exp * 1000;
};

// Fails open: an expiry we cannot read answers false, since a false "expired"
// signs a genuine customer out.
export const isPassExpired = (token: string): boolean => {
  const expiresAt = passExpiresAt(token);

  if (expiresAt === null) {
    return false;
  }

  return Date.now() > expiresAt + CLOCK_SKEW_TOLERANCE_SECONDS * 1000;
};

// Fails toward refreshing. Safe only because the caller keeps the pass in hand
// when the refresh fails — see `hobbii-starter.ts`.
export const isPassFresh = (token: string): boolean => {
  const expiresAt = passExpiresAt(token);

  if (expiresAt === null) {
    return false;
  }

  return Date.now() < expiresAt - FRESHNESS_MARGIN_SECONDS * 1000;
};
